import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Hash,
  MagnifyingGlass,
  Plus,
  PencilSimple,
  Trash,
  CircleNotch,
  FloppyDisk,
  X,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function normalizeCode(raw) {
  let c = (raw || "").trim().toLowerCase();
  if (!c) return "";
  if (!c.startsWith("#")) c = "#" + c;
  c = "#" + c.slice(1).replace(/[^a-z0-9_]/g, "");
  return c;
}

export default function ShortcutsDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // shortcode object or 'new'
  const [form, setForm] = useState({ code: "", reply: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/shortcodes");
      setItems(r.data || []);
    } catch (e) {
      toast.error("Failed to load shortcuts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      setQuery("");
      setEditing(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (s) => s.code.toLowerCase().includes(q) || (s.reply || "").toLowerCase().includes(q),
    );
  }, [items, query]);

  const startNew = () => {
    setEditing("new");
    setForm({ code: "", reply: "" });
  };

  const startEdit = (sc) => {
    setEditing(sc);
    setForm({ code: sc.code, reply: sc.reply });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ code: "", reply: "" });
  };

  const save = async () => {
    const code = normalizeCode(form.code);
    const reply = (form.reply || "").trim();
    if (code.length < 2) {
      toast.error("Shortcode must be at least 1 character after #");
      return;
    }
    if (!reply) {
      toast.error("Reply text is required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/shortcodes", { code, reply });
        toast.success(`Created ${code}`);
      } else {
        await api.patch(
          `/shortcodes/${encodeURIComponent(editing.code)}`,
          { code, reply },
        );
        toast.success(`Updated ${code}`);
      }
      await load();
      cancelEdit();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (sc) => {
    if (!window.confirm(`Delete ${sc.code}?`)) return;
    try {
      await api.delete(`/shortcodes/${encodeURIComponent(sc.code)}`);
      toast.success(`Deleted ${sc.code}`);
      await load();
      if (editing && editing !== "new" && editing.code === sc.code) cancelEdit();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="shortcuts-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash size={18} weight="fill" className="text-[#25D366]" />
            Quick Shortcuts
          </DialogTitle>
          <DialogDescription>
            Type <code className="px-1 py-0.5 rounded bg-[var(--bg)] text-[11px]">#code</code>{" "}
            in the chat input to instantly insert these replies. All agents share this list.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col md:flex-row gap-3 flex-1 overflow-hidden">
          {/* List */}
          <div className="md:w-1/2 flex flex-col min-h-0">
            <div className="flex gap-2 mb-2">
              <div className="flex-1 relative">
                <MagnifyingGlass
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]"
                />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search code or text…"
                  className="h-9 pl-8 text-xs"
                  data-testid="shortcuts-search"
                />
              </div>
              <Button
                size="sm"
                onClick={startNew}
                className="h-9 gap-1 bg-[#25D366] hover:bg-[#1da851] text-white"
                data-testid="shortcuts-new-btn"
              >
                <Plus size={12} weight="bold" /> New
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto border border-[var(--border)] rounded-md scroll-thin">
              {loading ? (
                <div className="p-6 text-center text-xs text-[var(--muted)]">
                  <CircleNotch size={16} className="animate-spin inline mr-1" /> Loading…
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-6 text-center text-xs text-[var(--muted)]">
                  No shortcuts found.
                </div>
              ) : (
                <ul>
                  {filtered.map((sc) => {
                    const isActive = editing && editing !== "new" && editing.code === sc.code;
                    return (
                      <li
                        key={sc.code}
                        className={`border-b border-[var(--border)] last:border-0 ${
                          isActive ? "bg-[var(--bg)]" : "hover:bg-[var(--bg)]"
                        }`}
                      >
                        <div className="p-2.5 flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(sc)}
                            className="flex-1 text-left min-w-0"
                            data-testid={`shortcut-row-${sc.code}`}
                          >
                            <div className="text-xs font-mono font-semibold text-[#25D366]">
                              {sc.code}
                            </div>
                            <div className="text-[11px] text-[var(--muted)] line-clamp-2 mt-0.5 whitespace-pre-wrap">
                              {sc.reply}
                            </div>
                          </button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => startEdit(sc)}
                            title="Edit"
                          >
                            <PencilSimple size={12} />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 text-red-500 hover:text-red-600"
                            onClick={() => remove(sc)}
                            title="Delete"
                          >
                            <Trash size={12} />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-2">
              {items.length} shortcut{items.length !== 1 ? "s" : ""} total
            </div>
          </div>

          {/* Editor */}
          <div className="md:w-1/2 border border-[var(--border)] rounded-md p-3 flex flex-col min-h-0">
            {!editing ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-2 text-[var(--muted)]">
                <Hash size={32} />
                <div className="text-sm">Select a shortcut to edit</div>
                <div className="text-[11px]">or click "New" to create one</div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold heading">
                    {editing === "new" ? "New shortcut" : `Edit ${editing.code}`}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={cancelEdit}
                    title="Cancel"
                  >
                    <X size={14} />
                  </Button>
                </div>
                <label className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-semibold mb-1">
                  Shortcode
                </label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  placeholder="#mycode"
                  className="h-9 mb-3 font-mono text-sm"
                  data-testid="shortcut-code-input"
                />
                <label className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-semibold mb-1">
                  Reply text
                </label>
                <Textarea
                  value={form.reply}
                  onChange={(e) => setForm((f) => ({ ...f, reply: e.target.value }))}
                  placeholder="Reply message…"
                  className="flex-1 min-h-[180px] text-sm resize-none"
                  data-testid="shortcut-reply-input"
                />
                <div className="text-[11px] text-[var(--muted)] mt-1.5">
                  Tip: use placeholders like{" "}
                  <code className="px-1 py-0.5 rounded bg-[var(--bg)]">[Date]</code> or{" "}
                  <code className="px-1 py-0.5 rounded bg-[var(--bg)]">[Fare Amount]</code> —
                  agents can edit them before sending.
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={save}
                    disabled={saving}
                    className="flex-1 h-9 gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white"
                    data-testid="shortcut-save-btn"
                  >
                    {saving ? (
                      <CircleNotch size={14} className="animate-spin" />
                    ) : (
                      <FloppyDisk size={14} weight="fill" />
                    )}
                    {editing === "new" ? "Create" : "Save"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={cancelEdit}
                    className="h-9"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
